import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { cardTypes, mapStatTypeToStatsApi } from "../shared/utils";
import LeaderCardLoading from "./components/LeaderCardLoading/LeaderCardLoading";
import PlayerLeaderData from "./components/PlayerLeaderData";
import ClubLeaderData from "./components/ClubLeaderData";
import { useReactAppState } from "../shared/containers/utils";
import getSortedData, { LeaderCardContext } from "./utils";
import useMLSStatistics from "./hooks/useMLSStatistics";
import useForgeClubs from "../mls-standings/hooks/useForgeClubs";
import useMLSPlayerStatistics from "./hooks/useMLSPlayerStatistics";
import { statsTypeMap } from "../shared/api/mlsStatsMap";
import { apiURL, d3PlayerByIdsAPI } from "../shared/api/variables";
import { fetchClient } from "../shared/api/fetching";
import { IsMlsCompetition } from "../../libraries/_helpers";

function LeaderCard({ options }) {
  const { t } = useReactAppState();
  const { season } = options

  const {
    cardType,
    competitionSportecId,
    statViewType,
    advertiser,
    statTableUrl,
    mlsPhaseType,
    clubSportecId,
  } = options;

  const [currentStatViewType, setCurrentStatViewType] = useState(mapStatTypeToStatsApi(statViewType));

  const sortColumn = useMemo(() => {
    if (currentStatViewType?.value === "first_and_second_assists" && !IsMlsCompetition(options.competitionSportecId)) {
      return "assists";

    }
    
    return statsTypeMap[currentStatViewType?.value] || currentStatViewType?.value;
  }, [currentStatViewType, options.competitionSportecId]);



  const { data: mlsStatisticsData, isLoading: mlsStatisticsLoading, isError: mlsStatisticsError } =
    useMLSStatistics({ competitionSportecId, seasonSportecId: season, orderBy: sortColumn, cardType, queryOptions: { retry: false } });

  const { data: mlsPlayerStatisticsData, isLoading: mlsPlayerStatisticsLoading, isError: mlsPlayerStatisticsError } =
    useMLSPlayerStatistics({ competitionSportecId, seasonSportecId: season, sortColumn, cardType, queryOptions: { retry: false }, formatPlayers: false, clubSportecId });

  const playerIds = useMemo(() => {
    return mlsPlayerStatisticsData?.map(({ player_id }) => player_id);
  }, [mlsPlayerStatisticsData]);

  const d3PlayersEndpoint = d3PlayerByIdsAPI({ playerIds });

  const { data: d3Players } =
    useQuery({
      queryKey: ["d3-players", { playerIds }],
      queryFn: () => fetchClient({ endpoint: d3PlayersEndpoint, apiURL: apiURL.d3SportsAPI }),
      enabled: !!(playerIds?.length) && cardType === cardTypes.player,
      select: (data) => {
        const playerList = new Map(data?.map(player => [player.sportecId, player]));
        return playerList;
      }
    });

  const mergedPlayers = useMemo(() => {
    return mlsPlayerStatisticsData?.map((player) => {
      const d3Player = d3Players?.get(player.player_id);
      return {
        ...player,
        image: d3Player?.thumbnail,
        playerSlug: d3Player?.playerSlug
      }
    });
  }, [d3Players, mlsPlayerStatisticsData]);


  const mlsStatistics = useMemo(() => {
    return getSortedData({ data: mlsStatisticsData, orderBy: sortColumn });
  }, [mlsStatisticsData, sortColumn]);

  const clubList = useMemo(() => {
    return mlsStatistics?.team_statistics?.map(({ team_id }) => {
      return { club_id: team_id };
    });
  }, [mlsStatistics]);

  const { data: d3Clubs, isLoading: d3ClubsLoading, isError: d3ClubsError } = useForgeClubs({ clubs: clubList });

  const clubs = useMemo(() => {
    const { team_statistics } = mlsStatistics || {};
    const mlsClubList = new Map(d3Clubs?.map(club => [club.sportecId, club]));

    return team_statistics?.map((clubStats) => {
      const d3Club = mlsClubList.get(clubStats.team_id);
      return {
        ...clubStats,
        club: {
          ...d3Club,
          fullName: clubStats.team_name,
          shortName: clubStats.team_three_letter_code,
          id: clubStats.team_id,
          sportecId: clubStats.team_id,
          abbreviation: clubStats.team_three_letter_code,
        }
      }
    });
  }, [d3Clubs, mlsStatistics]);

  useEffect(() => {
    setCurrentStatViewType(mapStatTypeToStatsApi(statViewType));
  }, [statViewType]);

  if (mlsStatisticsLoading || d3ClubsLoading) {
    return <LeaderCardLoading cardType={cardType} advertiser={advertiser} statTableUrl={statTableUrl} currentStatViewType={t(currentStatViewType.key)} />;
  }

  if (mlsStatisticsError || d3ClubsError) {
    return null;
  }

  const [first, ...rest] = cardType === cardTypes.player ? mergedPlayers || [] : clubs || [];
  const footer = t(currentStatViewType.key);

  return (
    first && rest?.length > 0 ? (
      <div role="region" className="mls-o-leader-card">
        <div className="d3-o-media-object d3-o-media-object--vertical">
          <LeaderCardContext.Provider value={{
            first,
            rest,
            footer,
            currentStatType: sortColumn,
            advertiser,
            header: t(currentStatViewType.key),
            clubsData: clubs,
            mlsPhaseType,
            statTableUrl
          }}>
            {
              cardType === cardTypes.player ?
                <PlayerLeaderData />
                : <ClubLeaderData />
            }
          </LeaderCardContext.Provider>
        </div>
      </div>
    ) : null
  );
}

// eslint-disable-next-line no-func-assign
LeaderCard = React.memo(LeaderCard);

export default LeaderCard;
